import React, { useState, useRef, useCallback } from "react"
import { COLOR_CONSTANTS } from "../../../constants/ColorConstants"
import SVG from "react-inlinesvg"
import { toAbsoluteUrl } from "../../../_metronic/_helpers/index.js"
import { ICON_CONSTANTS } from "../../../constants/ImageConstants.js"

// ── Design tokens (from tailwind.config.cjs) ─────────────────────────────────
const tokens = {
  color: {
    primaryMain: "#C85400",
    primaryLight: "#FB9500",
    secondaryMain: COLOR_CONSTANTS.PRIMARY_GREEN,
    secondaryDark: "#539737",
    headerBg: "#059669"
  },
  shadow: {
    fab: "0 25px 50px -12px rgba(0,0,0,0.25)",
    card: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)"
  },
  radius: {
    full: "9999px",
    xl: "16px",
    lg: "8px"
  }
}

// ── Static styles object ──────────────────────────────────────────────────────
const styles = {
  fab: {
    position: "fixed",
    bottom: "84px",
    right: "20px",
    zIndex: 999,
    backgroundColor: tokens.color.secondaryMain+"aa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: tokens.radius.full,
    border: "none",
    cursor: "pointer",
    boxShadow: tokens.shadow.fab,
    transition: "all 0.3s ease-out",
    outline: "none"
  },

  fabIcon: {
    width: "24px",
    height: "24px",
    color: "#ffffff",
    transition: "transform 0.3s ease",
    flexShrink: 0
  },

  overlay: (isOpen) => ({
    position: "fixed",
    inset: 0,
    zIndex: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "opacity 0.3s ease",
    pointerEvents: isOpen ? "auto" : "none",
    opacity: isOpen ? 1 : 0
  }),

  card: (isOpen) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: tokens.radius.xl,
    overflow: "hidden",
    width: "90vw",
    height: "90vh",
    background: "#0f0f13",
    border: "1px solid rgba(99,102,241,0.25)",
    boxShadow: tokens.shadow.card,
    transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease",
    transform: isOpen ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
    opacity: isOpen ? 1 : 0
  }),

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    flexShrink: 0,
    backgroundColor: tokens.color.headerBg
  },

  trafficLights: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },

  trafficDot: (color) => ({
    width: "12px",
    height: "12px",
    borderRadius: tokens.radius.full,
    backgroundColor: color,
    display: "inline-block",
    flexShrink: 0
  }),

  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: tokens.radius.lg,
    border: `1px solid rgba(200,84,0,0.25)`,
    background: "rgba(200,84,0,0.2)",
    color: tokens.color.primaryMain,
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
    flexShrink: 0
  },

  closeIcon: {
    width: "16px",
    height: "16px",
    flexShrink: 0
  },

  iframe: {
    flex: 1,
    width: "100%",
    border: "none",
    background: "#ffffff",
    display: "block",
    marginBottom: "-3px"
  },

  bottomGlow: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${tokens.color.primaryMain}, ${tokens.color.primaryLight}, transparent)`
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const DEFAULT_URL = process.env.REACT_APP_SUPPORT_CHAT_URL || "https://maxxi-support-agent-theta.vercel.app/"

export const MaxxiSupportWidget = ({ url = DEFAULT_URL, title = "Support Modal" }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [fabHovered, setFabHovered] = useState(false)
  const [closeBtnHovered, setCloseBtnHovered] = useState(false)
  const iframeRef = useRef(null)

  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) closeModal()
    },
    [closeModal]
  )

  return (
    <>
      {/* FAB Button */}
      <button
        onClick={openModal}
        aria-label="Open external content"
        style={{
          ...styles.fab,
          ...(fabHovered && {
            transform: "scale(1.3)",
            backgroundColor: tokens.color.secondaryDark
          })
        }}
        onMouseEnter={() => setFabHovered(true)}
        onMouseLeave={() => setFabHovered(false)}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.95)")}
        onMouseUp={(e) =>
          (e.currentTarget.style.transform = fabHovered ? "scale(1.1)" : "scale(1)")
        }>
        <SVG
          src={toAbsoluteUrl(ICON_CONSTANTS.CUSTOMER_SERVICE)}
          style={{
            filter: "brightness(0) invert(1)",
            transform: fabHovered ? "rotate(12deg)" : "rotate(0deg)"
          }}
        />
      </button>

      {/* Modal Overlay — always mounted, visibility toggled via CSS */}
      <div
        onClick={handleBackdropClick}
        aria-modal="true"
        role="dialog"
        aria-label={title}
        style={styles.overlay(isOpen)}>
        {/* Card */}
        <div style={styles.card(isOpen)}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.trafficLights}>
              <span style={styles.trafficDot("rgba(239,68,68,0.7)")} />
              <span style={styles.trafficDot("rgba(234,179,8,0.7)")} />
              <span style={styles.trafficDot("rgba(34,197,94,0.7)")} />
            </div>

            {/* Close Button */}
            <button
              onClick={closeModal}
              aria-label="Close modal"
              style={{
                ...styles.closeBtn,
                ...(closeBtnHovered && {
                  transform: "scale(1.1)",
                  background: "rgba(239,68,68,0.28)",
                  borderColor: "rgba(239,68,68,0.5)",
                  color: "#f87171"
                })
              }}
              onMouseEnter={() => setCloseBtnHovered(true)}
              onMouseLeave={() => setCloseBtnHovered(false)}
              onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.9)")}
              onMouseUp={(e) =>
                (e.currentTarget.style.transform = closeBtnHovered ? "scale(1.1)" : "scale(1)")
              }>
              <svg
                style={styles.closeIcon}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* iFrame — always mounted, never re-renders */}
          <iframe
            ref={iframeRef}
            src={url}
            title={title}
            style={styles.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />

          <div style={styles.bottomGlow} />
        </div>
      </div>
    </>
  )
}
